import type { ComponentType, Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Drawer, Paper, Tab, Tabs, useTheme } from "@mui/material";
import DrawerHeader from "shared-ui/src/components/detail-drawer/DrawerHeader";
import DrawerContentContainer from "shared-ui/src/components/detail-drawer/DrawerContentContainer";
import { SaveButton } from "shared-ui/src/components/SaveButton";
import AssetLifecycleManagementDrawerEvents from "./AssetLifecycleManagementDrawerEvents";
import {
  AssetLifecycleManagementDrawerTabName,
  AssetLifecycleManagementDrawerTabNames,
  AssetLifecycleManagementDrawerTabHeaders,
  LIFECYCLE_MANAGEMENT_EVENT_DATA_SOURCE,
  LifecycleEventDateDialogControls,
} from "./constants";
import AssetLifecycleManagementDrawerOrders from "./AssetLifecycleManagementDrawerOrders";
import AssetLifecycleManagementDrawerActivity from "./AssetLifecycleManagementDrawerActivity";
import { LifecycleManagementAllEvents } from "@lib/ShiOneClient";
import ManufacturerLogo from "shared-ui/src/components/ManufacturerLogo";
import AssetLifecycleManagementDrawerLineItems from "./AssetLifecycleManagementDrawerLineItems";

const [EVENT, ORDERS, ASSETS, ACTIVITY] =
  AssetLifecycleManagementDrawerTabNames;

const tabComponents: Record<
  AssetLifecycleManagementDrawerTabName,
  ComponentType<ActiveComponentProps>
> = {
  [EVENT]: AssetLifecycleManagementDrawerEvents,
  [ORDERS]: AssetLifecycleManagementDrawerOrders,
  [ASSETS]: AssetLifecycleManagementDrawerLineItems,
  [ACTIVITY]: AssetLifecycleManagementDrawerActivity,
};

interface ActiveComponentProps {
  selectedLifecycleEventData: LifecycleManagementAllEvents | undefined;
  setSelectedLifecycleEventData?: Dispatch<
    SetStateAction<LifecycleManagementAllEvents | undefined>
  >;
  onSaveHandlerChange?: (handler: (() => void) | undefined) => void;
  onSaveStateChange?: (disable: boolean, saving: boolean) => void;
  onSaveSuccess?: () => void;
  hasAssetOrderDataManagePermission?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  eventDateDialog?: LifecycleEventDateDialogControls;
}

export function AssetLifecycleManagementDetailDrawer({
  open,
  handleClose,
  selectedLifecycleEventData,
  setSelectedLifecycleEventData,
  hasAssetOrderDataManagePermission,
  initialTab,
  activeTab,
  onTabChange,
  requestClose,
  onDirtyChange,
  eventDateDialog,
}: Readonly<{
  open: boolean;
  handleClose: () => void;
  selectedLifecycleEventData: LifecycleManagementAllEvents | undefined;
  setSelectedLifecycleEventData: Dispatch<
    SetStateAction<LifecycleManagementAllEvents | undefined>
  >;
  hasAssetOrderDataManagePermission?: boolean;
  initialTab?: AssetLifecycleManagementDrawerTabName;
  /** Controls the tab from the host (e.g. the URL); omit for local tab state. */
  activeTab?: AssetLifecycleManagementDrawerTabName;
  onTabChange?: (tab: AssetLifecycleManagementDrawerTabName) => void;
  /** Close request from the X button, backdrop or Escape; defaults to `handleClose`. */
  requestClose?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  eventDateDialog?: LifecycleEventDateDialogControls;
}>) {
  const theme = useTheme();
  const [localTab, setLocalTab] = useState(0);
  const isTabControlled = activeTab !== undefined;
  const [activeSaveHandler, setActiveSaveHandler] = useState<
    (() => void) | undefined
  >();
  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isManualEvent =
    selectedLifecycleEventData?.source ===
    LIFECYCLE_MANAGEMENT_EVENT_DATA_SOURCE.ManuallyCreated;

  const visibleTabs = useMemo<readonly AssetLifecycleManagementDrawerTabName[]>(
    () =>
      isManualEvent
        ? AssetLifecycleManagementDrawerTabNames.filter(
            (name) => name !== ORDERS && name !== ASSETS,
          )
        : AssetLifecycleManagementDrawerTabNames,
    [isManualEvent],
  );

  useEffect(() => {
    if (!open || isTabControlled) return;
    const index = initialTab ? visibleTabs.indexOf(initialTab) : 0;
    setLocalTab(index >= 0 ? index : 0);
  }, [
    open,
    selectedLifecycleEventData?.id,
    initialTab,
    visibleTabs,
    isTabControlled,
  ]);

  const controlledIndex = isTabControlled ? visibleTabs.indexOf(activeTab) : 0;
  const tab = isTabControlled ? Math.max(controlledIndex, 0) : localTab;
  const setTab = useCallback(
    (index: number) => {
      if (isTabControlled) {
        const name = visibleTabs[index];
        if (name) onTabChange?.(name);
        return;
      }
      setLocalTab(index);
    },
    [isTabControlled, onTabChange, visibleTabs],
  );
  const resetLocalTab = useCallback(() => {
    if (!isTabControlled) setLocalTab(0);
  }, [isTabControlled]);
  const handleRequestClose = requestClose ?? handleClose;

  const clampedTab = Math.min(tab, visibleTabs.length - 1);
  const ActiveComponent = tabComponents[visibleTabs[clampedTab]];

  const handleSaveHandlerChange = useCallback(
    (handler: (() => void) | undefined) => {
      setActiveSaveHandler(() => handler);
    },
    [],
  );

  const handleSaveStateChange = useCallback(
    (disable: boolean, saving: boolean) => {
      setDisableSaveButton(disable);
      setIsSaving(saving);
    },
    [],
  );

  const handleSaveSuccess = useCallback(() => {
    handleClose();
    resetLocalTab();
  }, [handleClose, resetLocalTab]);

  const handleSaveClick = () => {
    activeSaveHandler?.();
    resetLocalTab();
  };

  const getSaveButtonLabel = (): string | undefined => {
    if (clampedTab !== 0) {
      return "Save unavailable - only available in Events tab";
    }
    if (!hasAssetOrderDataManagePermission) {
      return "Save unavailable - insufficient permissions";
    }
    return undefined;
  };

  const getDrawerHeaderIconElement = () => {
    return (
      <ManufacturerLogo
        manufacturer={selectedLifecycleEventData?.manufacturer}
        size="large"
        baseUrl={import.meta.env.LOGOS}
        hidePlaceholder
      />
    );
  };

  const getDrawerHeaderTitle = () => {
    return selectedLifecycleEventData?.manufacturer ?? currentTabConfig.header;
  };

  const isSaveDisabled = () => {
    return (
      clampedTab !== 0 ||
      disableSaveButton ||
      !hasAssetOrderDataManagePermission
    );
  };

  const currentTabConfig =
    AssetLifecycleManagementDrawerTabHeaders[visibleTabs[clampedTab]];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleRequestClose}
      role="dialog"
      aria-label="Asset Lifecycle Management Details"
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflow: "hidden",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <DrawerHeader
          iconElement={getDrawerHeaderIconElement()}
          header={getDrawerHeaderTitle()}
          subheader={currentTabConfig.subheader}
          onClose={() => {
            handleRequestClose();
            resetLocalTab();
          }}
        />
        <DrawerContentContainer
          sx={{
            flex: 1,
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
            }}
          >
            <Box
              id="lifecycle-drawer-tabs"
              sx={{
                flexShrink: 0,
                width: "100%",
              }}
            >
              <Paper square>
                <Tabs value={clampedTab} onChange={(_, v) => setTab(v)}>
                  {visibleTabs.map((name, index) => (
                    <Tab
                      key={name}
                      label={name}
                      value={index}
                      aria-controls={`lifecycle-panel-${index}`}
                    />
                  ))}
                </Tabs>
              </Paper>
            </Box>
            <Box
              id={`lifecycle-panel-${clampedTab}`}
              role="tabpanel"
              aria-labelledby="lifecycle-drawer-tabs"
              aria-label={`${AssetLifecycleManagementDrawerTabHeaders[visibleTabs[clampedTab]].subheader}`}
              sx={{
                typography: "body2",
                color: "text.secondary",
                backgroundColor: "table.header",
                flexGrow: 1,
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              {ActiveComponent && (
                <ActiveComponent
                  selectedLifecycleEventData={selectedLifecycleEventData}
                  setSelectedLifecycleEventData={setSelectedLifecycleEventData}
                  onSaveHandlerChange={handleSaveHandlerChange}
                  onSaveStateChange={handleSaveStateChange}
                  onSaveSuccess={handleSaveSuccess}
                  hasAssetOrderDataManagePermission={
                    hasAssetOrderDataManagePermission
                  }
                  onDirtyChange={onDirtyChange}
                  eventDateDialog={eventDateDialog}
                />
              )}
            </Box>
            <Box
              id="lifecycle-drawer-actions"
              sx={{
                borderTop: 1,
                borderColor: "divider",
                p: 2,
                display: "flex",
                justifyContent: "flex-end",
                backgroundColor: "background.paper",
                flexShrink: 0,
                mt: "auto",
                width: "100%",
              }}
            >
              <SaveButton
                onClick={handleSaveClick}
                disabled={isSaveDisabled()}
                isSaving={isSaving}
                aria-label={getSaveButtonLabel()}
              />
            </Box>
          </Box>
        </DrawerContentContainer>
      </Box>
    </Drawer>
  );
}
